import { useEffect, useMemo, useState } from "react";
import { useStore } from "../state/store";
import { SPAGHETTI_MODELS, ENSEMBLE_SOURCES, ensembleColor, ensembleName } from "../data/liveApi";
import { ensembleSets } from "../engine/liveGuidance";
import GenesisWatchPanel from "./GenesisWatchPanel";

const titleCase = (s: string) =>
  s === "UNNAMED" ? "Unnamed" : s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const POLL_MS = 10 * 60 * 1000; // auto-refresh cadence (backend caches 15 min)

const BASINS = [
  { code: "AL", label: "Atlantic" },
  { code: "EP", label: "E. Pacific" },
  { code: "CP", label: "C. Pacific" },
];

// e.g. "ACTIVE: ATL 1 · EPAC 2" — basin-explicit so an all-Pacific list can't
// read as Atlantic activity
function activeHeader(index: { basin?: string }[]): string {
  const counts = { AL: 0, EP: 0, CP: 0 } as Record<string, number>;
  for (const s of index) counts[(s.basin ?? "AL")] = (counts[s.basin ?? "AL"] ?? 0) + 1;
  const parts = [`ATL ${counts.AL}`];
  if (counts.EP) parts.push(`EPAC ${counts.EP}`);
  if (counts.CP) parts.push(`CPAC ${counts.CP}`);
  return `ACTIVE: ${parts.join(" · ")}`;
}

export default function LiveSidebar() {
  const live = useStore((s) => s.live);
  const loadLiveIndex = useStore((s) => s.loadLiveIndex);
  const refreshLive = useStore((s) => s.refreshLive);
  const selectLiveStorm = useStore((s) => s.selectLiveStorm);
  const clearLiveStorm = useStore((s) => s.clearLiveStorm);
  const toggleModel = useStore((s) => s.toggleModel);
  const toggleAnalogs = useStore((s) => s.toggleAnalogs);
  const setAnalogTightness = useStore((s) => s.setAnalogTightness);
  const toggleAnalogMatchMode = useStore((s) => s.toggleAnalogMatchMode);
  const toggleEnsembleSource = useStore((s) => s.toggleEnsembleSource);
  const setShownEnsembles = useStore((s) => s.setShownEnsembles);
  const setEnsembleAlpha = useStore((s) => s.setEnsembleAlpha);
  const toggleRunScrub = useStore((s) => s.toggleRunScrub);
  const toggleOfficial = useStore((s) => s.toggleOfficial);
  const toggleAlerts = useStore((s) => s.toggleAlerts);
  const toggleSurge = useStore((s) => s.toggleSurge);
  const tracks = useStore((s) => s.tracks);
  const compare = useStore((s) => s.compare);
  const selectCompareStorm = useStore((s) => s.selectCompareStorm);
  const clearCompare = useStore((s) => s.clearCompare);
  const [compareSearch, setCompareSearch] = useState("");

  // search the full 1851+ track DB (capped) for a storm to compare
  const compareMatches = useMemo(() => {
    const q = compareSearch.trim().toLowerCase();
    if (q.length < 2) return [] as { storm_id: string; name: string; year: number; peak: number }[];
    const out: { storm_id: string; name: string; year: number; peak: number }[] = [];
    for (const s of tracks) {
      if (`${s.name} ${s.year}`.toLowerCase().includes(q)) {
        out.push({
          storm_id: s.storm_id, name: s.name, year: s.year,
          peak: s.track.length ? Math.max(...s.track.map((p) => p.vmax)) : 0,
        });
        if (out.length >= 40) break;
      }
    }
    return out;
  }, [tracks, compareSearch]);

  const compareStorm = compare.stormId ? tracks.find((t) => t.storm_id === compare.stormId) : null;
  const compareName = compareStorm ? `${titleCase(compareStorm.name)} (${compareStorm.year})` : compare.stormId ?? "";

  // load the storm list on first entry to live mode
  useEffect(() => {
    if (live.index.length === 0 && !live.loading) void loadLiveIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // background auto-refresh: new advisories appear without a manual click;
  // also notices when an off-season lull ends and a real storm spins up
  useEffect(() => {
    const t = setInterval(() => void refreshLive(), POLL_MS);
    return () => clearInterval(t);
  }, [refreshLive]);

  const sets = live.bundle ? ensembleSets(live.bundle) : [];
  // ensemble sources the backend tried but could not deliver — say why
  // instead of leaving a silent gap in the chip row
  const feedNotes = Object.entries(live.bundle?.ensemble_feeds ?? {})
    .filter(([src, f]) => ENSEMBLE_SOURCES.some((e) => e.source === src) && f.status !== "ok" && !sets.some((x) => x.source === src))
    .map(([src, f]) =>
      `${ensembleName(src)}: ${
        f.status === "unavailable" ? "upstream unavailable"
          : f.status === "no_match" ? "no track for this system this cycle"
            : f.status === "stale" ? "cycle too old — excluded"
              : f.status === "disabled" ? "feed disabled"
                : f.status}`);

  return (
    <>
      {live.indexKind === "demo" && live.error && (
        <div className="error" style={{ marginBottom: 8 }}>
          ⚠ {live.error}
        </div>
      )}
      <label className="muted xs">
        {live.indexKind === "active" ? activeHeader(live.index) : "NO ACTIVE STORMS — DEMO"}
      </label>
      {live.indexKind === "active" && !live.index.some((s) => (s.basin ?? "AL") === "AL") && (
        <p className="muted sm" style={{ marginTop: 2 }}>
          No Atlantic systems — listings below are Pacific basins.
        </p>
      )}
      {live.indexKind === "demo" && !live.error && (
        <p className="muted sm" style={{ marginTop: 2 }}>
          Off-season: pick a past storm to preview live forecasts + model spaghetti.
        </p>
      )}

      <select
        value={live.selectedId ?? ""}
        onChange={(e) => (e.target.value ? void selectLiveStorm(e.target.value) : clearLiveStorm())}
        className="storm-select"
      >
        <option value="">{live.indexKind === "active" ? "All systems (overview)" : "Select a storm…"}</option>
        {BASINS.map(({ code, label }) => {
          const group = live.index.filter((s) => (s.basin ?? "AL") === code);
          if (group.length === 0) return null;
          return (
            <optgroup key={code} label={label}>
              {group.map((s) => (
                <option key={s.storm_id} value={s.storm_id}>
                  {s.label ?? `${s.name ?? s.storm_id} (${s.year ?? ""})`}
                </option>
              ))}
            </optgroup>
          );
        })}
        {/* demo entries (and anything with an unknown basin code) */}
        {live.index
          .filter((s) => !BASINS.some((b) => b.code === (s.basin ?? "AL")))
          .map((s) => (
            <option key={s.storm_id} value={s.storm_id}>
              {s.label ?? `${s.name ?? s.storm_id} (${s.year ?? ""})`}
            </option>
          ))}
        {/* curated past storms — always reachable, even while a storm is live */}
        {live.indexKind === "active" && live.demoStorms.length > 0 && (
          <optgroup label="Demo · past storms">
            {live.demoStorms.map((s) => (
              <option key={`demo-${s.storm_id}`} value={s.storm_id}>
                {s.name ?? s.storm_id}{s.year ? ` (${s.year})` : ""}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      <button className="refresh-btn" onClick={() => void loadLiveIndex()}>🔄 Refresh</button>

      <hr />
      <label className="muted xs">🏛️ OFFICIAL NHC</label>
      <button
        className={live.showOfficial ? "chip active" : "chip"}
        style={{ width: "100%", marginTop: 4 }}
        onClick={toggleOfficial}
      >
        {live.showOfficial ? "Cone + watches/warnings: ON" : "Cone + watches/warnings: OFF"}
      </button>
      {live.showOfficial && live.officialGis?.advisory && (
        <p className="muted xs" style={{ marginTop: 4 }}>
          Advisory #{live.officialGis.advisory.num} · {live.officialGis.advisory.date}
        </p>
      )}
      {live.showOfficial && live.officialGis && !live.officialGis.cone && (
        <p className="muted xs" style={{ marginTop: 4 }}>
          No official cone yet — invests get one at the first advisory.
        </p>
      )}
      {live.showOfficial && (live.officialGis?.ww.length ?? 0) > 0 && (
        <p className="muted xs" style={{ marginTop: 4 }}>
          <span style={{ color: "#DC2626" }}>▬</span> Hur warn{" "}
          <span style={{ color: "#FB7185" }}>▬</span> Hur watch{" "}
          <span style={{ color: "#3B82F6" }}>▬</span> TS warn{" "}
          <span style={{ color: "#FDE047" }}>▬</span> TS watch
        </p>
      )}
      <p className="muted xs" style={{ marginTop: 6 }}>
        White cone = NHC's official forecast area. The coloured lines are
        TODAY'S model + ensemble guidance — different products; comparing them is the point.
      </p>
      <button
        className={live.surge?.available && live.showSurge ? "chip active" : "chip"}
        style={{ width: "100%", marginTop: 6 }}
        onClick={toggleSurge}
        disabled={!live.surge?.available}
        title="NHC Potential Storm Surge Flooding map: reasonable-worst-case water height above ground"
      >
        {live.surge?.available
          ? `Surge flooding map (adv ${live.surge.adv}): ${live.showSurge ? "ON" : "OFF"}`
          : live.surge ? "Surge flooding map: none issued" : "Surge flooding map: checking…"}
      </button>
      {live.surge?.available && live.showSurge && (
        <p className="muted xs" style={{ marginTop: 4, lineHeight: 1.6, textTransform: "none", letterSpacing: 0 }}>
          Water above ground:{" "}
          {[["#3B82F6", "> 1 ft"], ["#FACC15", "> 3 ft"], ["#F97316", "> 6 ft"], ["#DC2626", "> 9 ft"]].map(([c, l]) => (
            <span key={l} style={{ whiteSpace: "nowrap", marginRight: 6 }}><span style={{ color: c }}>■</span> {l}</span>
          ))}
          <br />Reasonable worst case (1-in-10 chance of exceeding). Zoom in to the coast.
          {live.surge.current_adv != null && Number(live.surge.adv) < live.surge.current_adv - 2 && (
            <span style={{ color: "#FBBF24" }}> Issued with advisory {live.surge.adv}; the latest is {live.surge.current_adv}.</span>
          )}
        </p>
      )}
      <button
        className={live.showAlerts ? "chip active" : "chip"}
        style={{ width: "100%", marginTop: 6 }}
        onClick={() => void toggleAlerts()}
      >
        {live.showAlerts ? `NWS warnings (${live.alerts.length}): ON` : "NWS tornado/flood warnings"}
      </button>

      <hr />
      <label className="muted xs">🕑 RUN-TO-RUN HISTORY</label>
      {live.cyclesLoading ? (
        <p className="muted sm" style={{ marginTop: 4 }}>Checking logged cycles…</p>
      ) : live.cycles.length >= 2 ? (
        <>
          <button
            className={live.runScrubActive ? "chip active" : "chip"}
            style={{ width: "100%", marginTop: 4 }}
            onClick={() => void toggleRunScrub()}
          >
            {live.runScrubActive
              ? `Run history: ON (${live.cycles.length} cycles)`
              : `🎞️ Scrub ${live.cycles.length} past cycles`}
          </button>
          <p className="muted xs" style={{ marginTop: 6 }}>
            Step back through past advisory cycles — watch the models + official
            forecast shift run-to-run (violet = the prior run's track).
          </p>
        </>
      ) : (
        <p className="muted sm" style={{ marginTop: 4 }}>
          No multi-cycle history logged for this storm yet.
        </p>
      )}

      <hr />
      <label className="muted xs">🆚 COMPARE A PAST STORM</label>
      {compare.stormId ? (
        <div style={{ marginTop: 4 }}>
          <div
            className="chip active"
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span style={{ color: "#F59E0B" }}>● {compareName}</span>
            <span role="button" onClick={clearCompare} style={{ cursor: "pointer", marginLeft: 8 }} title="Remove comparison">✕</span>
          </div>
          {compare.loading && <div className="muted sm" style={{ marginTop: 4 }}>Loading replay…</div>}
          <p className="muted xs" style={{ marginTop: 6 }}>
            Amber = its real track.{" "}
            {compare.source === "track" ? "Pre-2004: track only." : "Scrub it with the slider on the map."}
          </p>
        </div>
      ) : (
        <>
          <input
            className="storm-select"
            style={{ marginTop: 4 }}
            placeholder="🔍 Search any storm (e.g. Sandy)"
            value={compareSearch}
            onChange={(e) => setCompareSearch(e.target.value)}
          />
          {compareSearch.trim().length >= 2 && (
            <div className="explorer-list" style={{ maxHeight: 200, overflowY: "auto" }}>
              {compareMatches.map((s) => (
                <button key={s.storm_id} className="explorer-row" onClick={() => void selectCompareStorm(s.storm_id)}>
                  <span className="ename">{titleCase(s.name)} ({s.year})</span>
                  <span className="muted emeta">{s.peak} kt</span>
                </button>
              ))}
              {compareMatches.length === 0 && (
                <div className="muted sm" style={{ padding: 8 }}>No storms match.</div>
              )}
            </div>
          )}
          <p className="muted xs" style={{ marginTop: 6 }}>
            Overlay any 1851+ storm's real track beside the live storm to contrast paths.
          </p>
        </>
      )}

      <hr />
      <label className="muted xs">📡 MODEL GUIDANCE</label>
      <div className="preset-grid">
        {SPAGHETTI_MODELS.map((m) => {
          // a model with no track for this storm (NHC's file has only its
          // starting point: its tracker hasn't followed a weak, broad low)
          const noTrack = !!live.bundle && (live.bundle.model_runs[m.tech]?.points.length ?? 0) < 2;
          const derived = live.bundle?.model_runs[m.tech]?.derived;
          return (
            <button
              key={m.tech}
              className={(live.models.includes(m.tech) ? "chip active" : "chip") + (noTrack ? " na" : "")}
              style={live.models.includes(m.tech) ? { borderColor: m.color, color: "white" } : undefined}
              onClick={() => toggleModel(m.tech)}
              title={noTrack ? `No ${m.name} track for this storm in NHC's latest data`
                : derived ? `${m.name}: ${derived}` : undefined}
            >
              <span style={{ color: m.color }}>●</span> {m.name}
            </button>
          );
        })}
      </div>
      <p className="muted xs" style={{ marginTop: 8, lineHeight: 1.6 }}>
        White line = NHC official forecast · points D / S / H / M = depression, storm, hurricane, major.
        Dots on the model lines = where each model has the storm at the same moments, a day apart
        (hover one for the time) — dots spread wider apart = a faster storm.
        Observed track by intensity:{" "}
        {[["#95A5A6", "TD"], ["#2ECC71", "TS"], ["#F1C40F", "1"], ["#F39C12", "2"], ["#E67E22", "3"], ["#E74C3C", "4"], ["#C084FC", "5"]].map(([c, l]) => (
          <span key={l} style={{ whiteSpace: "nowrap", marginRight: 5 }}><span style={{ color: c }}>●</span> {l}</span>
        ))}
      </p>

      <hr />
      <label className="muted xs">🌐 ENSEMBLE MEMBERS</label>
      {sets.length > 0 ? (
        <>
          <div className="preset-grid">
            {sets.map((s) => {
              const on = live.shownEnsembles.includes(s.source);
              const color = ensembleColor(s.source);
              return (
                <button
                  key={s.source}
                  className={on ? "chip active" : "chip"}
                  style={on ? { borderColor: color, color: "white" } : undefined}
                  onClick={() => toggleEnsembleSource(s.source)}
                  title={s.label}
                >
                  <span style={{ color }}>●</span> {ensembleName(s.source)} {s.n_members}{s.matched_by === "formation" && s.ens_size ? `/${s.ens_size}` : ""}
                </button>
              );
            })}
          </div>
          <p className="muted xs" style={{ marginTop: 6 }}>
            <span role="button" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setShownEnsembles(sets.map((s) => s.source))}>all</span>
            {" · "}
            <span role="button" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setShownEnsembles([])}>none</span>
            {" · "}off when a storm opens — tap a set to add its members. Each line = one member's track. Physics: GEFS, ECMWF ENS. AI: AIFS, DeepMind.
            Spread within and between ensembles is the uncertainty signal — none of it is the NHC cone.
          </p>
          <label className="muted xs" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span style={{ whiteSpace: "nowrap" }}>line intensity ×{live.ensembleAlpha.toFixed(1)}</span>
            <input
              type="range" min={0.5} max={3} step={0.1} value={live.ensembleAlpha}
              onChange={(e) => setEnsembleAlpha(Number(e.target.value))}
              style={{ flex: 1 }}
              title="Member-line opacity. Big ensembles are drawn fainter per line so 1000 members read as a cloud; raise this when the tails feel too subtle."
            />
          </label>
        </>
      ) : (
        <p className="muted sm" style={{ marginTop: 4 }}>No ensemble members in this storm's guidance.</p>
      )}
      {feedNotes.length > 0 && (
        <p className="muted xs" style={{ marginTop: 4, color: "#FBBF24" }}>{feedNotes.join(" · ")}</p>
      )}
      {sets.some((s) => s.attribution) && (
        <p className="muted xs" style={{ marginTop: 4 }}>
          DeepMind members: {sets.find((s) => s.attribution)!.attribution}{" "}
          <a href={sets.find((s) => s.terms_url)?.terms_url ?? "#"} target="_blank" rel="noreferrer">terms ↗</a>
        </p>
      )}

      <hr />
      <label className="muted xs">🔥 TRAJECTORY ANALOGS</label>
      <button
        className={live.showAnalogs ? "chip active" : "chip"}
        style={{ width: "100%", marginTop: 4 }}
        onClick={toggleAnalogs}
        disabled={!live.bundle}
      >
        {live.showAnalogs ? "Analogs: ON" : "Find trajectory analogs"}
      </button>
      <p className="muted xs" style={{ marginTop: 6 }}>
        1851+ storms on a similar path to this one — and where they went next.
      </p>
      {live.showAnalogs && (
        <div style={{ marginTop: 8 }}>
          <div className="muted xs" style={{ marginBottom: 4 }}>MATCH BY ORIGIN BOX</div>
          <div className="preset-grid">
            <button
              className={live.analogMatchMode === "origin" ? "chip active" : "chip"}
              onClick={() => live.analogMatchMode !== "origin" && toggleAnalogMatchMode()}
            >
              Born here
            </button>
            <button
              className={live.analogMatchMode === "transit" ? "chip active" : "chip"}
              onClick={() => live.analogMatchMode !== "transit" && toggleAnalogMatchMode()}
            >
              Passed through
            </button>
          </div>
          <p className="muted xs" style={{ marginTop: 4 }}>
            {live.analogMatchMode === "origin"
              ? "Strict: storms BORN in the origin box, then a similar path."
              : "Looser (Bayesian): storms that PASSED THROUGH the origin box — any birthplace — then a similar path."}
          </p>

          <div className="muted xs" style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
            <span>◀ Tighter</span><span>Looser ▶</span>
          </div>
          <input
            type="range" min={0.6} max={1.8} step={0.1} value={live.analogTightness}
            onChange={(e) => setAnalogTightness(Number(e.target.value))}
            style={{ width: "100%", accentColor: "#F59E0B" }}
          />
        </div>
      )}

      <GenesisWatchPanel />

      {live.error && <div className="error">Live error: {live.error}</div>}
      {live.loading && <div className="muted sm" style={{ marginTop: 8 }}>Loading…</div>}
    </>
  );
}
